'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { gatewayClient } from '../../api';
import { useErrorMessage } from '../../hooks/useErrorMessage';
import styles from './OtpStepUpModal.module.css';

// Risk-band step-up at checkout (docs/features/risk-fraud.md): the backend
// rejected the buy with RISK_ORDER_OTP_REQUIRED (fresh email/phone code
// needed) or RISK_SEPA_PHONE_VERIFICATION_REQUIRED (no verified phone on the
// account). Codes confirm through the shared /user-verification/:otp
// endpoint; a confirm younger than the BE freshness window satisfies the
// band, so onVerified() simply retries the checkout.
export type OtpChallenge = {
  mode: 'codes' | 'phoneVerify';
  emailRequired: boolean;
  phoneRequired: boolean;
};

interface OtpStepUpModalProps {
  challenge: OtpChallenge;
  profileEmail?: string;
  profilePhone?: string;
  onClose: () => void;
  // All required confirms passed - caller retries the checkout.
  onVerified: () => void;
}

type Step =
  | { kind: 'phoneInput' }
  | { kind: 'code'; channel: 'email' | 'phone' };

const buildSteps = (challenge: OtpChallenge): Step[] => {
  if (challenge.mode === 'phoneVerify') {
    // Number first, then its code. The confirm both verifies the account
    // phone AND counts as a fresh phone OTP server-side (one SMS total).
    return [{ kind: 'phoneInput' }, { kind: 'code', channel: 'phone' }];
  }
  const steps: Step[] = [];
  if (challenge.emailRequired) steps.push({ kind: 'code', channel: 'email' });
  if (challenge.phoneRequired) steps.push({ kind: 'code', channel: 'phone' });
  return steps;
};

export const OtpStepUpModal: React.FC<OtpStepUpModalProps> = ({
  challenge,
  profileEmail,
  profilePhone,
  onClose,
  onVerified,
}) => {
  const t = useTranslations('cart');
  const resolveError = useErrorMessage();

  const [steps] = useState<Step[]>(() => buildSteps(challenge));
  const [stepIndex, setStepIndex] = useState(0);
  const [phone, setPhone] = useState(profilePhone ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards the StrictMode double-mount from sending every code twice.
  const initiatedRef = useRef(false);
  // A confirm may still be in flight when the user closes the modal; its
  // resolution must not advance steps or retry the checkout.
  const closedRef = useRef(false);

  const step = steps[stepIndex];

  const close = () => {
    closedRef.current = true;
    onClose();
  };

  const initiateForChannel = useCallback(async (channel: 'email' | 'phone') => {
    if (channel === 'email') {
      await gatewayClient.verification.initiateOrderEmailOtp();
    } else {
      await gatewayClient.verification.initiateOrderPhoneOtp();
    }
  }, []);

  // 'codes' mode: fire the first send automatically; later steps send when
  // reached (sequential - one code on screen at a time).
  useEffect(() => {
    if (!step || step.kind !== 'code' || challenge.mode !== 'codes') return;
    if (stepIndex === 0 && initiatedRef.current) return;
    initiatedRef.current = true;
    setError(null);
    initiateForChannel(step.channel).catch((err) => {
      setError(resolveError(err));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  // Enter in the inputs calls these directly, so they repeat the buttons'
  // disabled conditions - without the guards, Enter double-submits (burning
  // the single-use code) or submits a short code.
  const handleSendPhoneCode = async () => {
    if (busy || !phone.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await gatewayClient.verification.startPhoneVerification(phone.trim());
      setStepIndex(1);
      setCode('');
    } catch (err) {
      setError(resolveError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCode = async () => {
    if (busy || code.trim().length < 6) return;
    setBusy(true);
    setError(null);
    try {
      await gatewayClient.verification.confirmOtp(code.trim());
      if (closedRef.current) return;
      setCode('');
      if (stepIndex + 1 < steps.length) {
        setStepIndex(stepIndex + 1);
      } else {
        onVerified();
      }
    } catch (err) {
      setError(resolveError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (challenge.mode === 'phoneVerify') {
        await gatewayClient.verification.startPhoneVerification(phone.trim());
      } else if (step?.kind === 'code') {
        await initiateForChannel(step.channel);
      }
    } catch (err) {
      setError(resolveError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!step) return null;

  const isPhoneInput = step.kind === 'phoneInput';
  const channel = step.kind === 'code' ? step.channel : 'phone';
  const target = channel === 'email' ? profileEmail : phone || profilePhone;

  return (
    <div className={styles.overlay} onClick={close}>
      <div className={styles.paper} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {challenge.mode === 'phoneVerify'
              ? t('summary.otp.phoneVerifyTitle')
              : t('summary.otp.title')}
          </h2>
          <button
            className={styles.closeButton}
            onClick={close}
            aria-label={t('summary.otp.close')}
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          {steps.length > 1 && (
            <span className={styles.stepBadge}>
              {stepIndex + 1} / {steps.length}
            </span>
          )}

          {isPhoneInput ? (
            <>
              <p className={styles.description}>{t('summary.otp.phoneVerifyHint')}</p>
              <input
                className={styles.input}
                type="tel"
                autoComplete="tel"
                placeholder="+40712345678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSendPhoneCode();
                }}
              />
              {error && <p className={styles.error}>{error}</p>}
              <button
                className={styles.primaryButton}
                onClick={handleSendPhoneCode}
                disabled={busy || !phone.trim()}
              >
                {t('summary.otp.sendCode')}
              </button>
            </>
          ) : (
            <>
              <p className={styles.description}>
                {channel === 'email'
                  ? t('summary.otp.emailCodeHint')
                  : t('summary.otp.phoneCodeHint')}
                {target ? <span className={styles.target}> {target}</span> : null}
              </p>
              <input
                className={styles.codeInput}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleConfirmCode();
                }}
              />
              {error && <p className={styles.error}>{error}</p>}
              <button
                className={styles.primaryButton}
                onClick={handleConfirmCode}
                disabled={busy || code.trim().length < 6}
              >
                {t('summary.otp.confirm')}
              </button>
              <button
                className={styles.resendButton}
                onClick={handleResend}
                disabled={busy}
              >
                {t('summary.otp.resend')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
